import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { usePanelSecrets, useSaveSecret, useDeleteSecret, useRevealSecret, useImageBudget, useSetImageBudget } from "../../hooks/usePanelSecrets";
import { errorMessage } from "../../hooks/useServers";

const REVEAL_SECONDS = 30;

// Pantalla «Claves»: las claves/tokens del panel (Gemini, GitHub, Cloudflare, Vultr…). Todo se guarda cifrado, la IA nunca lo
// ve y la lista solo muestra los últimos 4 caracteres. Como pediste, cada clave nueva también se copia en el 09_Credenciales_y_Accesos.md de Drive.
export function PanelSecretsDialog({ open, onOpenChange }) {
  const { data: secrets } = usePanelSecrets(open);
  const save = useSaveSecret();
  const del = useDeleteSecret();
  const reveal = useRevealSecret();

  const { data: budget } = useImageBudget(open);
  const setBudget = useSetImageBudget();
  const [capInput, setCapInput] = useState("");
  const [capMsg, setCapMsg] = useState("");
  const [addKey, setAddKey] = useState("gemini_api_key");
  const [addLabel, setAddLabel] = useState("");
  const [addValue, setAddValue] = useState("");
  const [addMsg, setAddMsg] = useState("");
  const [shown, setShown] = useState({}); // key -> valor visible (se oculta solo)
  const [confirmDelete, setConfirmDelete] = useState("");

  useEffect(() => {
    const keys = Object.keys(shown);
    if (!keys.length) return undefined;
    const timer = setTimeout(() => setShown({}), REVEAL_SECONDS * 1000);
    return () => clearTimeout(timer);
  }, [shown]);

  const submitSecret = async () => {
    setAddMsg("");
    try {
      await save.mutateAsync({ key: addKey === "otra" ? "otra" : addKey, secret: addValue.trim(), label: addKey === "otra" ? addLabel.trim() : "" });
      setAddValue("");
      setAddLabel("");
      setAddMsg("Guardada ✅ (cifrada, y con copia en el 09_ de Drive).");
    } catch (e) {
      setAddMsg(errorMessage(e, "No se pudo guardar la clave."));
    }
  };

  const saveCap = async () => {
    setCapMsg("");
    const value = Number(String(capInput).replace(",", "."));
    try {
      await setBudget.mutateAsync(value);
      setCapInput("");
      setCapMsg("Tope guardado ✅");
    } catch (e) {
      setCapMsg(errorMessage(e, "No se pudo guardar el tope."));
    }
  };

  const doReveal = async (key) => {
    try {
      const data = await reveal.mutateAsync(key);
      setShown({ [key]: data.secret });
    } catch (e) {
      setAddMsg(errorMessage(e, "No se pudo mostrar la clave."));
    }
  };

  const doDelete = async (key) => {
    await del.mutateAsync(key);
    setConfirmDelete("");
    setShown({});
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="secrets-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Claves y tokens</DialogTitle>
          <DialogDescription>
            Claves de las herramientas que usa el panel (Gemini para imágenes con IA, GitHub, Cloudflare, Vultr…). El servidor del taller se configura en el botón «Taller». Se guardan cifradas, la IA nunca las ve, y cada una también se copia en el 09_Credenciales_y_Accesos.md de Drive.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 max-h-[65vh] overflow-y-auto pr-1">
          <section data-testid="secrets-keys" className="space-y-2">
            <p className="text-sm font-medium">Claves y tokens</p>
            <div data-testid="secrets-list" className="space-y-1">
              {(secrets?.secrets || []).length === 0 && <p className="text-xs text-muted-foreground">Todavía no hay claves guardadas.</p>}
              {(secrets?.secrets || []).map((s) => (
                <div key={s.key} data-testid={`secret-row-${s.key}`} className="rounded-lg border border-border p-2 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs font-medium truncate">{s.label}</p>
                      <p className="text-xs text-muted-foreground font-mono">
                        {shown[s.key] ? <span data-testid={`secret-value-${s.key}`} className="break-all">{shown[s.key]}</span> : s.hint} {s.updated_at ? `· ${s.updated_at}` : ""}
                      </p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {shown[s.key] ? (
                        <Button data-testid={`secret-hide-${s.key}`} size="sm" variant="outline" onClick={() => setShown({})}>Ocultar</Button>
                      ) : (
                        <Button data-testid={`secret-reveal-${s.key}`} size="sm" variant="outline" onClick={() => doReveal(s.key)}>Ver</Button>
                      )}
                      {confirmDelete === s.key ? (
                        <Button data-testid={`secret-delete-confirm-${s.key}`} size="sm" variant="destructive" onClick={() => doDelete(s.key)}>¿Seguro? Borrar</Button>
                      ) : (
                        <Button data-testid={`secret-delete-${s.key}`} size="sm" variant="outline" onClick={() => setConfirmDelete(s.key)}>Borrar</Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-lg border border-border p-3 space-y-2">
              <p className="text-xs font-medium">Agregar o cambiar una clave</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label htmlFor="secret-kind">¿Cuál?</Label>
                  <select id="secret-kind" data-testid="secret-add-select" className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm" value={addKey} onChange={(e) => setAddKey(e.target.value)}>
                    {(secrets?.catalog || []).map((c) => (
                      <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                    <option value="otra">Otra (escribe el nombre)</option>
                  </select>
                </div>
                {addKey === "otra" && (
                  <div className="space-y-1">
                    <Label htmlFor="secret-label">Nombre</Label>
                    <Input id="secret-label" data-testid="secret-add-label" value={addLabel} onChange={(e) => setAddLabel(e.target.value)} placeholder="Ej. Token de WaSender" />
                  </div>
                )}
                <div className={`space-y-1 ${addKey === "otra" ? "col-span-2" : ""}`}>
                  <Label htmlFor="secret-value">Clave o token</Label>
                  <Input id="secret-value" data-testid="secret-add-value" type="password" autoComplete="new-password" value={addValue} onChange={(e) => setAddValue(e.target.value)} />
                </div>
              </div>
              <Button data-testid="secret-add-button" size="sm" onClick={submitSecret} disabled={save.isPending || !addValue.trim() || (addKey === "otra" && !addLabel.trim())}>
                {save.isPending ? "Guardando..." : "Guardar"}
              </Button>
              {addMsg && <p data-testid="secret-add-message" className="text-xs text-muted-foreground">{addMsg}</p>}
            </div>
          </section>

          <section data-testid="images-budget" className="space-y-2">
            <p className="text-sm font-medium">Imágenes con IA (Gemini)</p>
            <div className="rounded-lg border border-border p-3 space-y-2">
              {typeof budget?.spent_usd === "number" ? (
                <>
                  <p data-testid="images-budget-today" className="text-sm">
                    Hoy llevas <strong>${budget.spent_usd.toFixed(2)}</strong> de <strong>${budget.cap_usd.toFixed(2)}</strong> ({budget.count} {budget.count === 1 ? "imagen" : "imágenes"}). Quedan ${budget.remaining_usd.toFixed(2)}.
                  </p>
                  <p data-testid="images-budget-prices" className="text-xs text-muted-foreground">
                    Costo estimado por imagen: ${budget.price_normal_usd.toFixed(3)} normal, ${budget.price_high_usd.toFixed(3)} alta calidad, ${budget.price_premium_usd.toFixed(3)} premium. Al llegar al tope el Orquestador se detiene y te avisa; solo tú puedes subirlo aquí. Es una estimación: la factura real es la de Google.
                  </p>
                  <div className="flex items-end gap-2">
                    <div className="space-y-1">
                      <Label htmlFor="images-cap">Tope diario (USD, de {budget.min_usd} a {budget.max_usd})</Label>
                      <Input id="images-cap" data-testid="images-cap-input" className="w-32" inputMode="decimal" value={capInput} onChange={(e) => setCapInput(e.target.value)} placeholder={String(budget.cap_usd)} />
                    </div>
                    <Button data-testid="images-cap-save" size="sm" onClick={saveCap} disabled={setBudget.isPending || !capInput.trim()}>{setBudget.isPending ? "Guardando..." : "Guardar tope"}</Button>
                  </div>
                  {capMsg && <p data-testid="images-cap-message" className="text-xs text-muted-foreground">{capMsg}</p>}
                </>
              ) : (
                <p className="text-xs text-muted-foreground">Cargando…</p>
              )}
            </div>
          </section>
        </div>

        <DialogFooter>
          <Button data-testid="secrets-close-button" variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
